/* eslint-disable brace-style */
/* eslint-disable no-restricted-globals */
/* eslint-disable max-len */
import React, {
  useState, useRef, useEffect, useCallback, useMemo,
} from 'react';
import { X, Square, SquareCheck } from 'tabler-icons-react';
import {
  Box, Popover, useMantineColorScheme, NumberInput, RangeSlider,
} from '@mantine/core';
import { assetPrefix, renderCompactNumber } from '../../../utilities/general';
import NewRangePopoverContent from './NewRangePopoverContent';

const NewAssetDropDown = (props) => {
  const {
    placeholder,
    keyName,
    setValue,
    value,
    maxSelections = 5,
    enableRangeFilter = false,
    status = [],
    title,
    originalData,
    popoverOpenRef,
    singleSelectWithRange = false,
    selectedTextColor = '#2E2E2EDE',
    selectedBgColor = '#EEF4FF',
    placeholderTextColor = '#898989',
    displayMapping = {},
    colorMapping = {},
    customWidth = 'w-36',
    dropdownWidth = 'w-[203px]',
    popoverWidth = 300,
    dropdownMaxHeight = 'max-h-72',
    currency,
    parentKey,
    childKey,
  } = props;

  // State management
  const [tempMin, setTempMin] = useState(0);
  const [tempMax, setTempMax] = useState(0);
  const [range, setRange] = useState('');
  const [minError, setMinError] = useState('');
  const [maxError, setMaxError] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedAssets, setSelectedAssets] = useState(Array.isArray(value) && value.length > 0 && typeof value[0] === 'string' ? value : []);
  const [min, setMin] = useState(null);
  const [max, setMax] = useState(null);
  const [popoverOpened, setPopoverOpened] = useState(false);
  const [hasValidated, setHasValidated] = useState(false);
  const [hasRange, setHasRange] = useState(false);
  const [rangeBounds, setRangeBounds] = useState({ minBound: 0, maxBound: 100 });
  const [searchTerm, setSearchTerm] = useState('');

  const { colorScheme } = useMantineColorScheme();

  // Refs
  const dropdownRef = useRef(null);
  const isUpdatingRef = useRef(false);

  const isStatusSelection = useMemo(() => status && status.length > 0, [status]);

  const items = useMemo(() => {
    if (isStatusSelection) return status;
    if (!originalData) return [];
    return [...new Set(originalData.map((item) => item?.[keyName]).filter(Boolean))];
  }, [isStatusSelection, status, originalData, keyName]);

  // Calculate min/max bounds based on selected status
  const calculateRangeBounds = useCallback(() => {
    if (!originalData || !parentKey || !childKey) return { minBound: 0, maxBound: 100 };

    if (selectedAssets.length === 0) return { minBound: 0, maxBound: 100 };

    const values = originalData.map((item) => {
      const rawValue = item[parentKey]?.[childKey];
      return typeof rawValue === 'number' ? rawValue : 0;
    }).filter(Boolean);

    if (values.length === 0) return { minBound: 0, maxBound: 100 };

    if (selectedAssets[0] === 'In Profit') {
      // For profit status, min is 0, max is highest positive value
      const maxPositive = Math.max(...values.filter((v) => v > 0));
      return { minBound: 0, maxBound: maxPositive > 0 ? maxPositive : 100 };
    }

    if (selectedAssets[0] === 'In Loss') {
      // For loss status, min is lowest negative value, max is 0
      const minNegative = Math.min(...values.filter((v) => v < 0));
      return { minBound: minNegative < 0 ? minNegative : -100, maxBound: 0 };
    }

    return { minBound: Math.min(...values), maxBound: Math.max(...values) };
  }, [originalData, parentKey, childKey, selectedAssets]);

  useEffect(() => {
    if (value !== undefined && !isUpdatingRef.current) {
      // Handle array with range values
      if (Array.isArray(value) && value.length === 2 && typeof value[0] === 'number') {
        const [minValue, maxValue] = value;
        setMin(minValue);
        setMax(maxValue);
        setTempMin(minValue);
        setTempMax(maxValue);
        setRange(`${renderCompactNumber(minValue, currency)} - ${renderCompactNumber(maxValue, currency)}`);
        setHasRange(true);

        // Determine status based on range
        let statusValue = '';
        if (minValue >= 0) {
          statusValue = 'In Profit';
        } else if (maxValue <= 0) {
          statusValue = 'In Loss';
        }

        if (statusValue && !selectedAssets.includes(statusValue)) {
          setSelectedAssets([statusValue]);
        }
      }
      // Handle string status values
      else if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'string') {
        setSelectedAssets(value);

        // If we have a previous range saved, keep it
        if (!hasRange && min === null && max === null) {
          setHasRange(false);
        }
      }
      // Handle empty/null values
      else if (!value || (Array.isArray(value) && value.length === 0)) {
        setSelectedAssets([]);
        setMin(null);
        setMax(null);
        setTempMin(0);
        setTempMax(0);
        setRange('');
        setHasRange(false);
      }
    }
  }, [value, currency]);

  // Update ranges and bounds when popover opens
  useEffect(() => {
    if (popoverOpened && hasRange && min !== null && max !== null) {
      // Restore temp values from saved range when popover opens
      setTempMin(min);
      setTempMax(max);
    } else if (popoverOpened && !hasRange && selectedAssets.length > 0) {
      // Initialize with bounds based on current selection
      const bounds = calculateRangeBounds();
      setTempMin(bounds.minBound);
      setTempMax(bounds.maxBound);
    }
  }, [popoverOpened, hasRange, min, max, calculateRangeBounds, selectedAssets]);

  useEffect(() => {
    if (popoverOpenRef) {
      popoverOpenRef.current = popoverOpened;
    }
  }, [popoverOpened, popoverOpenRef]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setPopoverOpened(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update bounds when status changes
  useEffect(() => {
    if (selectedAssets.length > 0 && isStatusSelection) {
      const bounds = calculateRangeBounds();
      setRangeBounds(bounds);

      // Only set these values if we don't already have a range
      if (!hasRange) {
        setTempMin(bounds.minBound);
        setTempMax(bounds.maxBound);
      }
    }
  }, [selectedAssets, calculateRangeBounds, isStatusSelection, hasRange]);

  useEffect(() => {
    if (isUpdatingRef.current) return;
    isUpdatingRef.current = true;

    if (hasRange && min !== null && max !== null) {
      // If we have a range, send the numeric range values
      setValue([min, max]);
    } else if (selectedAssets.length > 0) {
      // If we only have status, send the status values
      setValue(selectedAssets);
    } else {
      // Clear all filters
      setValue([]);
    }

    setTimeout(() => {
      isUpdatingRef.current = false;
    }, 10);
  }, [selectedAssets, min, max, setValue, hasRange]);

  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
    }
  }, [isOpen]);

  const toggleDropdown = useCallback(() => {
    if (isStatusSelection) {
      // If we have a range already, open the popover to edit range
      if (hasRange && selectedAssets.length > 0) {
        setPopoverOpened((prev) => !prev);
        setIsOpen(false);
      }
      // Otherwise show the status dropdown
      else if (isOpen) {
        setIsOpen(false);
      } else {
        setIsOpen(true);
        setPopoverOpened(false);
      }
    } else if (enableRangeFilter && !isStatusSelection) {
      setPopoverOpened((prev) => !prev);
      setIsOpen(false);
    } else {
      // Default multi-select logic
      setIsOpen((prev) => !prev);
      setPopoverOpened(false);
    }
  }, [isStatusSelection, enableRangeFilter, hasRange, selectedAssets.length, isOpen]);

  const handleAssetSelection = useCallback((asset) => {
    if (isStatusSelection) {
      // Single selection mode for status
      const newSelectedAssets = selectedAssets.includes(asset) ? [] : [asset];
      setSelectedAssets(newSelectedAssets);
      setIsOpen(false);

      // Calculate range bounds based on status
      if (enableRangeFilter && singleSelectWithRange && newSelectedAssets.length > 0) {
        // If we don't have a range already, show the popover
        if (!hasRange) {
          // Reset any existing range
          setMin(null);
          setMax(null);
          setRange('');

          // Show range popover after selecting a status
          setTimeout(() => {
            setPopoverOpened(true);
          }, 100);
        }
      }
    } else {
      // Multi-selection mode for assets
      setSelectedAssets((prevSelected) => {
        if (prevSelected.includes(asset)) {
          return prevSelected.filter((item) => item !== asset);
        }
        if (prevSelected.length < maxSelections) {
          return [...prevSelected, asset];
        }
        return prevSelected;
      });
    }
  }, [isStatusSelection, enableRangeFilter, singleSelectWithRange, selectedAssets, maxSelections, hasRange]);

  const clearSelections = useCallback((e) => {
    e.stopPropagation();

    setIsOpen(false);
    setPopoverOpened(false);
    setMin(null);
    setMax(null);
    setTempMin(0);
    setTempMax(0);
    setRange('');
    setMinError('');
    setMaxError('');
    setSelectedAssets([]);
    setHasRange(false);
    setValue([]);
  }, [setValue]);

  const validateRange = (minVal, maxVal, showErrors = false) => {
    let isValid = true;
    const parsedMin = parseFloat(minVal);
    const parsedMax = parseFloat(maxVal);

    if (minVal === '' || isNaN(parsedMin)) {
      if (showErrors) setMinError('Minimum is required');
      isValid = false;
    } else {
      setMinError('');
    }

    if (maxVal === '' || isNaN(parsedMax)) {
      if (showErrors) setMaxError('Maximum is required');
      isValid = false;
    } else {
      setMaxError('');
    }

    if (isValid && parsedMin > parsedMax) {
      if (showErrors) {
        setMinError('Minimum cannot be greater than maximum');
        setMaxError('Maximum cannot be less than minimum');
      }
      isValid = false;
    }

    return isValid;
  };

  const handleConfirm = () => {
    const isValid = validateRange(tempMin, tempMax, true);
    setHasValidated(true);
    if (!isValid) return;

    const parsedMin = parseFloat(tempMin);
    const parsedMax = parseFloat(tempMax);

    setMin(parsedMin);
    setMax(parsedMax);
    setRange(`${renderCompactNumber(parsedMin, currency)} - ${renderCompactNumber(parsedMax, currency)}`);
    setHasRange(true);
    setPopoverOpened(false);

    // Also update the parent value to include the range
    isUpdatingRef.current = true;
    setValue([parsedMin, parsedMax]);
    setTimeout(() => {
      isUpdatingRef.current = false;
    }, 10);
  };

  const handleClear = () => {
    setMin(null);
    setMax(null);
    setTempMin(rangeBounds.minBound);
    setTempMax(rangeBounds.maxBound);
    setRange('');
    setHasRange(false);

    // Keep the status selection
    isUpdatingRef.current = true;
    setValue(selectedAssets.length > 0 ? selectedAssets : []);
    setTimeout(() => {
      isUpdatingRef.current = false;
    }, 10);

    setMinError('');
    setMaxError('');
    setHasValidated(false);
    setPopoverOpened(false);
  };

  const handleClosePopover = () => {
    setPopoverOpened(false);

    // Reset temp values to current saved values
    if (hasRange && min !== null && max !== null) {
      setTempMin(min);
      setTempMax(max);
    } else {
      setTempMin(rangeBounds.minBound);
      setTempMax(rangeBounds.maxBound);
    }

    setMinError('');
    setMaxError('');
  };

  // Add this new useMemo for filtered items
  const filteredItems = useMemo(() => {
    if (!searchTerm.trim()) return items;

    return items.filter((item) => {
      const displayValue = displayMapping[item] || item;
      return displayValue.toLowerCase().includes(searchTerm.toLowerCase());
    });
  }, [items, searchTerm, displayMapping]);

  // Display value of selected items (from first component)
  const displayValueOfSelectedItems = useMemo(() => {
    // For status selection with color mapping (use case 2)
    if (isStatusSelection && colorMapping && Object.keys(colorMapping).length > 0 && selectedAssets.length > 0) {
      return (
        <div
          className="text-md font-semibold"
          style={{
            color: colorMapping[selectedAssets[0]]?.text || (colorScheme === 'dark' ? '#F4F4F5' : selectedTextColor),
          }}
        >
          {selectedAssets}
        </div>
      );
    }
    if (selectedAssets && selectedAssets.length > 0) {
      return (
        <div className="flex gap-2">
          <div className="text-md font-semibold text-[#33B1FF]">
            {selectedAssets.length <= 2 ? (
              selectedAssets.join(', ')
            ) : (
              <>
                {`${selectedAssets.slice(0, 2).join(', ')},`}
                {`+${selectedAssets.length - 2} others`}
              </>
            )}
          </div>
        </div>
      );
    }
    return null;
  }, [selectedAssets, colorMapping, colorScheme, selectedTextColor, isStatusSelection]);

  const displayText = useMemo(() => {
    // Show range if available
    if (hasRange && range && selectedAssets.length > 0) {
      return (
        selectedAssets?.map((selectedValue) => (
          <div
            key={selectedValue}
            className="font-medium text-md text-center text-[#2E2E2E] px-2 py-0.5 rounded-2xl"
            style={{
              backgroundColor: colorMapping[selectedValue]?.bg || (colorScheme === 'dark' ? '#1E293B' : selectedBgColor),
              color: colorMapping[selectedValue]?.text || (colorScheme === 'dark' ? '#F4F4F5' : selectedTextColor),
            }}
          >
            {range}
          </div>
        ))
      );
    }

    // Show selected status or assets
    if (selectedAssets.length > 0) {
      if (selectedAssets.length <= 4) {
        return (
          <div className="flex flex-wrap gap-1">
            {selectedAssets.map((selectedValue) => {
              const displayValue = displayMapping[selectedValue] || selectedValue;
              return (
                <div
                  key={selectedValue}
                  className="font-medium text-md text-center px-2 py-0.5 rounded-lg"
                  style={{
                    backgroundColor: colorMapping[selectedValue]?.bg || (colorScheme === 'dark' ? '#1E293B' : selectedBgColor),
                    color: colorMapping[selectedValue]?.text || (colorScheme === 'dark' ? '#F4F4F5' : selectedTextColor),
                  }}
                >
                  {displayValue}
                </div>
              );
            })}
          </div>
        );
      }

      return (
        <div
          className="text-md text-center text-[#2E2E2EDE] px-2 py-0.5 rounded-lg font-medium"
          style={{
            backgroundColor: colorScheme === 'dark' ? '#1E293B' : selectedBgColor,
            color: colorScheme === 'dark' ? '#F4F4F5' : selectedTextColor,
          }}
        >
          {`${selectedAssets.length} Selected`}
        </div>
      );
    }

    // Show placeholder
    return (
      <div className="inline-flex items-center font-medium min-w-fit px-2 py-0.5 text-md" style={{ color: placeholderTextColor }}>
        {placeholder}
      </div>
    );
  }, [
    selectedAssets,
    placeholder,
    hasRange,
    range,
    placeholderTextColor,
    displayMapping,
    colorMapping,
    selectedBgColor,
    selectedTextColor,
    colorScheme,
  ]);

  const dropdownItems = useMemo(() => {
    if (!isOpen) return null;

    return (
      <div className={`absolute top-15 left-0 ${dropdownWidth} ${colorScheme === 'dark' ? 'bg-black border-neutral-500' : 'bg-white border-gray-200'} border rounded-md shadow-lg z-10`}>
        {/* Search Input */}
        <div className="p-3 border-b border-gray-200 dark:border-neutral-500">
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={`w-full px-3 py-2 text-sm rounded-md border ${colorScheme === 'dark'
              ? 'bg-[#26282C] border-neutral-500 text-white placeholder-gray-400'
              : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
            } focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
            autoComplete="off"
          />
        </div>
        <div className={`${dropdownMaxHeight} overflow-y-auto`}>
          {filteredItems.length === 0 ? (
            <div className="px-5 py-2.5 text-sm font-medium text-center text-gray-500">
              {/* No data found */}
              {searchTerm.trim() ? 'No matching results found' : 'No data found'}
            </div>
          ) : (
            filteredItems.map((item) => {
              const isSelected = selectedAssets?.includes(item);
              const isDisabled = selectedAssets.length >= maxSelections && !isSelected && !isStatusSelection;
              const displayValue = displayMapping[item] || item;

              return (
                <div
                  key={item}
                  className={`px-5 py-2.5 flex gap-2.5 items-center border-b ${colorScheme === 'dark'
                    ? 'border-neutral-500 hover:bg-[#26282C]'
                    : 'border-gray-100 hover:bg-gray-50'} ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  onClick={() => !isDisabled && handleAssetSelection(item)}
                >
                  {isSelected ? (
                    <SquareCheck size={20} strokeWidth={2} color="#0093EF" />
                  ) : (
                    <Square size={20} strokeWidth={2} color="#898989" />
                  )}
                  <div className="text-sm font-normal text-[#3A3A3A] dark:text-white">{displayValue}</div>
                </div>
              );
            })
          )}
        </div>
      </div>
    );
  }, [
    isOpen,
    items,
    selectedAssets,
    maxSelections,
    isStatusSelection,
    displayMapping,
    handleAssetSelection,
    dropdownWidth,
    dropdownMaxHeight,
    colorScheme,
  ]);

  // Dynamically calculate range slider props based on selected status
  const getRangeSliderProps = useMemo(() => {
    const { minBound, maxBound } = rangeBounds;

    // Default step size
    let step = 0.5;

    // Adjust step size based on the range magnitude
    const rangeMagnitude = Math.abs(maxBound - minBound);
    if (rangeMagnitude > 1000) {
      step = 10;
    } else if (rangeMagnitude > 100) {
      step = 1;
    }

    return {
      min: minBound,
      max: maxBound,
      step,
      marks: [
        { value: minBound, label: renderCompactNumber(minBound, currency) },
        { value: maxBound, label: renderCompactNumber(maxBound, currency) },
      ],
    };
  }, [rangeBounds, currency]);

  return (
    <div className={`relative ${customWidth}`} ref={dropdownRef}>
      <Box className={`flex flex-col gap-3 border-b ${colorScheme === 'dark' && 'border-neutral-600'} pb-5`}>
        <div className="flex justify-between font-bold">
          <div>
            {title}
          </div>
          <div>
            {displayValueOfSelectedItems}
          </div>
        </div>
        <Popover
          opened={popoverOpened && enableRangeFilter}
          onChange={setPopoverOpened}
          position="bottom-start"
          width={popoverWidth}
          shadow="md"
          styles={{
            dropdown: {
              border: `1px solid ${colorScheme === 'dark' ? '#737373' : '#E8E8E8'}`,
            },
          }}
        >
          <Popover.Target>
            <div
              className={`h-12 px-1.5 py-1.5 border ${colorScheme === 'dark' ? 'border-neutral-500 bg-[#1E1E1E]' : 'bg-white border-[#E8E8E8]'}  rounded-md flex justify-between items-center cursor-pointer`}
              onClick={toggleDropdown}
            >
              {displayText}
              <div className="flex ml-1 gap-2 items-center">
                <img className="w-3 h-3" src={`${assetPrefix}/images/DownArrow.svg`} alt="downarrow" />
                <X
                  size={16}
                  onClick={clearSelections}
                  className="mr-1 text-gray-500 cursor-pointer"
                />
              </div>
            </div>
          </Popover.Target>
          {dropdownItems}
          {enableRangeFilter && (
            <Popover.Dropdown className={`h-auto w-[264px] ${colorScheme === 'dark' ? 'bg-[#1a1b1e]' : ''}  px-[29px] pt-4 pb-4 rounded-md`}>
              <Box
                className="cursor-pointer absolute top-2 right-2"
                onClick={handleClosePopover}
              >
                <X size={18} color="#898989" />
              </Box>
              <Box className="mb-10" w={230}>
                <RangeSlider
                  mt="xl"
                  styles={{
                    // Track styling (the background rail)
                    track: {
                      height: 6,
                      backgroundColor: colorScheme === 'dark' ? '#373A40' : '#f1f3f5',
                    },

                    // Filled bar styling (active portion of the slider)
                    bar: {
                      backgroundColor: '#33B1FF',
                      height: 6,
                    },

                    // Thumb styling (the draggable circles)
                    thumb: {
                      backgroundColor: '#33B1FF',
                      border: 'none',
                      width: 16,
                      height: 16,
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                    },

                    // Mark styling
                    mark: {
                      display: 'none', // Hide regular marks
                    },

                    // Filled mark styling
                    markFilled: {
                      border: 'none',
                      backgroundColor: 'transparent',
                    },

                    // Label styling
                    markLabel: {
                      fontSize: 14,
                      color: colorScheme === 'dark' ? '#fff' : '#000',
                      '&[data-value="0"]': {
                        transform: 'translateX(0)',
                        marginLeft: 0,
                      },
                    },
                    // Mark wrapper styling
                    markWrapper: {
                      position: 'absolute',
                      top: -1,
                      zIndex: 2,
                      marginTop: 10,
                      color: colorScheme === 'dark' ? '#fff' : '#000',
                      '&:first-of-type': {
                        left: '0%',
                      },
                      '&:last-of-type': {
                        left: '99.066765%',
                      },
                    },
                    // Label container
                    label: {
                      backgroundColor: '#33B1FF',
                      color: '#fff',
                      fontSize: 12,
                      height: 'auto',
                      padding: '2px 6px',
                      borderRadius: 4,
                      top: -32,
                      fontWeight: 500,
                    },
                    // Root container
                    root: {
                      paddingTop: 15,
                      paddingBottom: 15,
                    },
                  }}
                  minRange={0}
                  {...getRangeSliderProps}
                  value={[tempMin, tempMax]}
                  onChange={([minVal, maxVal]) => {
                    setTempMin(minVal);
                    setTempMax(maxVal);
                    if (hasValidated) validateRange(minVal, maxVal);
                  }}
                />
              </Box>
              <NewRangePopoverContent
                onConfirm={handleConfirm}
                onClear={handleClear}
              >
                <NumberInput
                  placeholder="Minimum"
                  value={tempMin}
                  onChange={(val) => {
                    setTempMin(val || 0);
                    if (hasValidated) validateRange(val, tempMax);
                  }}
                  parser={(val) => val
                    .replace(/[^0-9.-]/g, '')
                    .replace(/(?!^)-/g, '')
                    .replace(/(\..*)\./g, '$1')}
                  radius={6}
                  precision={2}
                  style={{ flex: 1 }}
                  error={minError}
                  hideControls
                />
                <NumberInput
                  placeholder="Maximum"
                  value={tempMax}
                  onChange={(val) => {
                    setTempMax(val || 0);
                    if (hasValidated) validateRange(tempMin, val);
                  }}
                  parser={(val) => val
                    .replace(/[^0-9.-]/g, '')
                    .replace(/(?!^)-/g, '')
                    .replace(/(\..*)\./g, '$1')}
                  radius={6}
                  precision={2}
                  style={{ flex: 1 }}
                  error={maxError}
                  hideControls
                />
              </NewRangePopoverContent>
            </Popover.Dropdown>
          )}
        </Popover>
      </Box>
    </div>
  );
};

export default React.memo(NewAssetDropDown);
